import { HttpStatus } from '@/constants/httpStatus.ts';
import type { RetryScope } from '@/client';

/**
 * Predicates and helpers for interpreting HTTP errors surfaced by the generated
 * client / axios. Rate limiting is app-wide (assistant, auth, tasks, uploads),
 * so these live here rather than beside any one feature.
 */

/** Checks if a response has a rate-limit status (429). */
export function isRateLimitError(error: unknown): boolean {
	return isStatus(error, HttpStatus.TOO_MANY_REQUESTS);
}

/**
 * Fallback submit-block window for a 429 that carries no server-advertised backoff. Rate-limited
 * forms must still block resubmission (instant retries extend the server-side window); short on
 * purpose so a user is never locked out much longer than the server intended.
 */
export const DEFAULT_RATE_LIMIT_BLOCK_SECONDS = 10;

/**
 * Extracts the server-advertised backoff from a 429 so callers can wait exactly
 * as long as the token bucket needs rather than guessing. Prefers the
 * `retry_after_seconds` body field, falling back to the `Retry-After` header.
 */
export function getRetryAfterSeconds(error: unknown): number | undefined {
	if (!error || typeof error !== 'object') return undefined;
	const response = (error as { response?: { data?: unknown; headers?: Record<string, unknown> } }).response;

	const data = response?.data;
	if (data && typeof data === 'object' && 'retry_after_seconds' in data) {
		const value = (data as { retry_after_seconds?: unknown }).retry_after_seconds;
		if (typeof value === 'number' && Number.isFinite(value)) return value;
	}

	const header = response?.headers?.['retry-after'];
	if (typeof header === 'string' || typeof header === 'number') {
		const parsed = Number(header);
		if (Number.isFinite(parsed)) return parsed;
	}

	return undefined;
}

/**
 * Whether a 429 clears on its own in seconds or has spent an allowance worth telling the user
 * about. The API decides it beside the bucket definitions it classifies, and says so on the body.
 *
 * Never re-derived from `retry_after_seconds`: whether a wait is short is a fact about a refill
 * rate, and a threshold living in this repo could not be tested against a policy living in another
 * — retuning a bucket would silently change what the UI claims.
 *
 * Absent reads as transient, which is what an older deployment or a differently-shaped 429 gets:
 * the wait still happens, and nothing is announced that cannot be substantiated.
 */
export function getRetryScope(error: unknown): RetryScope | undefined {
	if (!error || typeof error !== 'object') return undefined;
	const data = (error as { response?: { data?: unknown } }).response?.data;
	if (!data || typeof data !== 'object' || !('retry_scope' in data)) return undefined;

	const scope = (data as { retry_scope?: unknown }).retry_scope;
	return scope === 'transient' || scope === 'quota_exhausted' ? scope : undefined;
}

export function isQuotaExhausted(error: unknown): boolean {
	return getRetryScope(error) === 'quota_exhausted';
}

/**
 * Reads the status off either the axios error or its response, since the
 * generated client surfaces it in both shapes depending on the call path.
 */
function isStatus(error: unknown, status: HttpStatus): boolean {
	if (!error || typeof error !== 'object') return false;
	if ('status' in error && error.status === status) return true;
	const response = (error as { response?: { status?: number } }).response;
	return response?.status === status;
}

/**
 * The sentence the API sent with a refusal, when it sent one.
 *
 * Several routes answer a refusal a person can act on — a cap reached, an account not yet
 * finished — with wording chosen by the service that knows the rule. Surfacing that sentence
 * keeps one copy of it; a message written here would be a second copy able to drift from the
 * rule it describes.
 */
export function getErrorDetail(error: unknown): string | undefined {
	if (!error || typeof error !== 'object') return undefined;
	const data = (error as { response?: { data?: unknown } }).response?.data;
	if (!data || typeof data !== 'object' || !('detail' in data)) return undefined;

	const detail = (data as { detail?: unknown }).detail;
	return typeof detail === 'string' && detail.trim() ? detail : undefined;
}

/**
 * The sentence an OAuth route sent with a refusal.
 *
 * RFC 6749 gives these their own shape — `error` names the kind, `error_description` is the
 * sentence — so they arrive beside `detail` rather than in it. Read here for the same reason
 * `getErrorDetail` is: the wording belongs to the service enforcing the rule.
 */
export function getOAuthErrorDescription(error: unknown): string | undefined {
	if (!error || typeof error !== 'object') return undefined;
	const data = (error as { response?: { data?: unknown } }).response?.data;
	if (!data || typeof data !== 'object') return undefined;

	const description = (data as { error_description?: unknown }).error_description;
	return typeof description === 'string' && description.trim() ? description : undefined;
}
